import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import {
  Pencil, Trash2, RefreshCw, X, Check, FileText, FileCode,
  ChevronDown, ChevronUp, AlertCircle, Plus, Star, Globe
} from 'lucide-react';

// ── Language options ──────────────────────────────────────────
const LANGUAGE_OPTIONS = [
  { code: 'es', label: 'Español',    flag: 'ES' },
  { code: 'en', label: 'English',    flag: 'EN' },
  { code: 'fr', label: 'Français',   flag: 'FR' },
  { code: 'de', label: 'Deutsch',    flag: 'DE' },
  { code: 'it', label: 'Italiano',   flag: 'IT' },
  { code: 'pt', label: 'Português',  flag: 'PT' },
  { code: 'nl', label: 'Nederlands', flag: 'NL' },
  { code: 'other', label: 'Otro',    flag: '??' },
];

function getLangLabel(code) {
  const opt = LANGUAGE_OPTIONS.find(o => o.code === code);
  return opt ? opt.label : (code || '??').toUpperCase();
}

// ── Sera internal field definitions ──────────────────────────
const SERA_FIELDS = [
  { key: 'id',          label: 'ID del producto',   required: true },
  { key: 'title',       label: 'Título',             required: true },
  { key: 'price',       label: 'Precio',             required: true },
  { key: 'image_url',   label: 'URL de imagen',      required: true },
  { key: 'description', label: 'Descripción',        required: false },
  { key: 'brand',       label: 'Marca',              required: false },
  { key: 'category',    label: 'Categoría',          required: false },
  { key: 'link',        label: 'URL del producto',   required: false },
];

const FIELD_ALIASES = {
  id:          ['id', 'product_id', 'sku', 'gtin', 'item_id', 'uid', 'reference'],
  title:       ['title', 'name', 'product_name', 'nombre', 'titulo', 'titulo_producto'],
  price:       ['price', 'precio', 'selling_price', 'regular_price', 'sale_price', 'pvp'],
  image_url:   ['image_url', 'image', 'imagen', 'image_link', 'img_url', 'img', 'foto', 'picture', 'image_src'],
  description: ['description', 'descripcion', 'desc', 'short_description', 'descripcion_corta'],
  brand:       ['brand', 'marca', 'manufacturer', 'fabricante', 'vendor', 'maker'],
  category:    ['category', 'categoria', 'product_type', 'tipo', 'categoría'],
  link:        ['link', 'url', 'product_url', 'enlace', 'href', 'product_link', 'url_producto'],
};

function autoMap(headers) {
  const lower = headers.map(h => h.toLowerCase().trim());
  const mapping = {};
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    for (const alias of aliases) {
      const idx = lower.indexOf(alias);
      if (idx !== -1) { mapping[field] = headers[idx]; break; }
    }
  }
  return mapping;
}

// ── Column Mapping Form ───────────────────────────────────────
function ColumnMappingForm({ headers, mapping, onChange }) {
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <AlertCircle size={13} className="text-indigo-400" />
        <p className="text-gray-400 text-xs">
          Asocia las columnas de tu CSV a los campos de Sera. Los campos con <span className="text-red-400">*</span> son obligatorios.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SERA_FIELDS.map(({ key, label, required }) => (
          <div key={key}>
            <label className="text-gray-400 text-xs block mb-1">
              {label} {required && <span className="text-red-400">*</span>}
            </label>
            <select
              data-testid={`map-${key}`}
              value={mapping[key] || ''}
              onChange={e => onChange({ ...mapping, [key]: e.target.value || undefined })}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-xs outline-none focus:border-indigo-500"
            >
              {!required && <option value="">— no mapear —</option>}
              {required && !mapping[key] && <option value="">Selecciona columna...</option>}
              {headers.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Format badge ──────────────────────────────────────────────
function FormatBadge({ format }) {
  if (!format) return null;
  const isCSV = format === 'csv';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isCSV ? 'bg-emerald-500/15 text-emerald-400' : 'bg-blue-500/15 text-blue-400'
    }`}>
      {isCSV ? <FileText size={10} /> : <FileCode size={10} />}
      {isCSV ? 'CSV' : 'XML'}
    </span>
  );
}

// ── Language badge ────────────────────────────────────────────
function LangBadge({ code }) {
  const label = (LANGUAGE_OPTIONS.find(o => o.code === code)?.flag || (code || '??').toUpperCase()).slice(0, 2).toUpperCase();
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
      <Globe size={10} />
      {label}
    </span>
  );
}

const statusColor = { success: 'text-emerald-400', error: 'text-red-400', syncing: 'text-yellow-400', pending: 'text-gray-400', cancelled: 'text-orange-400' };
const statusLabel = { success: '✓ Sincronizado', error: '✗ Error', syncing: '↻ Sincronizando...', pending: '⏳ Pendiente', cancelled: '⊘ Cancelado' };

// ── Feed Card ─────────────────────────────────────────────────
function FeedCard({ config, onSync, onCancelSync, onDelete, onSetDefault, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const storedFormat = config.detected_format || null;
  const current = config.sync_progress_current || 0;
  const total = config.sync_progress_total || 0;
  const pct = total > 0 ? Math.round((current / total) * 100) : null;

  return (
    <div
      data-testid={`feed-card-${config.feed_config_id}`}
      className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden"
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-5 py-4">
        {/* Badges */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <LangBadge code={config.language} />
          {config.is_default && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20">
              <Star size={9} fill="currentColor" /> Por defecto
            </span>
          )}
          <FormatBadge format={storedFormat} />
        </div>

        {/* Name + status */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{config.feed_name}</p>
          <p className="text-gray-500 text-xs truncate">{config.feed_url}</p>
        </div>

        {/* Status */}
        <div className="text-right shrink-0">
          <p className={`text-xs font-medium ${statusColor[config.sync_status] || 'text-gray-400'}`}>
            {statusLabel[config.sync_status] || '—'}
          </p>
          {config.last_sync_count > 0 && config.sync_status !== 'syncing' && (
            <p className="text-gray-600 text-xs">{config.last_sync_count.toLocaleString()} prods.</p>
          )}
        </div>

        {/* Quick sync / cancel button always visible */}
        {config.sync_status === 'syncing' ? (
          <button
            data-testid={`feed-cancel-quick-${config.feed_config_id}`}
            onClick={() => onCancelSync(config.feed_config_id)}
            title="Detener importación"
            className="text-red-400/70 hover:text-red-400 p-1 rounded transition-colors shrink-0"
          >
            <X size={15} />
          </button>
        ) : (
          <button
            data-testid={`feed-sync-quick-${config.feed_config_id}`}
            onClick={() => onSync(config.feed_config_id)}
            title="Sincronizar ahora"
            className="text-indigo-400/60 hover:text-indigo-400 p-1 rounded transition-colors shrink-0"
          >
            <RefreshCw size={15} />
          </button>
        )}

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-gray-600 hover:text-gray-400 ml-1"
          data-testid={`feed-expand-${config.feed_config_id}`}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Progress bar while syncing */}
      {config.sync_status === 'syncing' && (
        <div className="px-5 pb-3 space-y-1.5" data-testid="sync-progress-container">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>
              {total > 0
                ? `${current.toLocaleString()} / ${total.toLocaleString()} productos`
                : 'Preparando importación...'}
            </span>
            {pct !== null && <span>{pct}%</span>}
          </div>
          <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
            <div
              data-testid="sync-progress-bar"
              className="bg-indigo-500 h-1.5 rounded-full transition-all duration-500"
              style={{
                width: pct !== null ? `${pct}%` : '60%',
                animation: pct === null ? 'pulse 1.5s infinite' : 'none'
              }}
            />
          </div>
        </div>
      )}

      {/* Error message */}
      {config.sync_error && (
        <div className="mx-5 mb-3 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <p className="text-red-400 text-xs font-mono">{config.sync_error}</p>
        </div>
      )}

      {/* Expanded action row */}
      {expanded && (
        <div className="border-t border-gray-800 px-5 py-3 flex flex-wrap gap-2 items-center">
          {/* Set default */}
          {!config.is_default && (
            <button
              data-testid={`feed-set-default-${config.feed_config_id}`}
              onClick={() => onSetDefault(config.feed_config_id)}
              className="flex items-center gap-1.5 text-xs text-amber-400/70 hover:text-amber-400 bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/20 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Star size={12} /> Marcar como predeterminado
            </button>
          )}

          {/* Sync / Cancel */}
          {config.sync_status === 'syncing' ? (
            <button
              data-testid={`feed-cancel-sync-${config.feed_config_id}`}
              onClick={() => onCancelSync(config.feed_config_id)}
              className="flex items-center gap-1.5 text-xs text-red-400/70 hover:text-red-400 bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 px-3 py-1.5 rounded-lg transition-colors"
            >
              <X size={12} /> Detener
            </button>
          ) : (
            <button
              data-testid={`feed-sync-${config.feed_config_id}`}
              onClick={() => onSync(config.feed_config_id)}
              className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-white bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 px-3 py-1.5 rounded-lg transition-colors"
            >
              <RefreshCw size={12} /> Sincronizar
            </button>
          )}

          {/* Edit */}
          <button
            data-testid={`feed-edit-${config.feed_config_id}`}
            onClick={() => onEdit(config)}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Pencil size={12} /> Editar
          </button>

          {/* Delete */}
          <button
            data-testid={`feed-delete-${config.feed_config_id}`}
            onClick={() => onDelete(config.feed_config_id)}
            className="flex items-center gap-1.5 text-xs text-red-400/60 hover:text-red-400 bg-gray-800 hover:bg-red-500/10 px-3 py-1.5 rounded-lg transition-colors ml-auto"
          >
            <Trash2 size={12} /> Eliminar
          </button>

          {/* Last sync info */}
          {config.last_sync_at && (
            <p className="w-full text-gray-600 text-xs mt-1">
              Última sync: {new Date(config.last_sync_at).toLocaleString('es-ES')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Add/Edit Feed Form ────────────────────────────────────────
function FeedForm({ existingLangs = [], editTarget = null, onSave, onCancel }) {
  const [form, setForm]               = useState({
    feed_name:      editTarget?.feed_name  || '',
    feed_url:       editTarget?.feed_url   || '',
    language:       editTarget?.language   || 'es',
    is_default:     editTarget?.is_default || false,
    customLang:     '',
  });
  const [saving, setSaving]           = useState(false);
  const [detecting, setDetecting]     = useState(false);
  const [detectedFormat, setDetected] = useState(null);
  const [csvHeaders, setCsvHeaders]   = useState([]);
  const [columnMapping, setMapping]   = useState({});
  const [showMapping, setShowMapping] = useState(false);
  const [error, setError]             = useState('');

  const isEdit = !!editTarget;
  const effectiveLang = form.language === 'other' ? form.customLang.trim().toLowerCase() : form.language;

  const handleDetect = async () => {
    if (!form.feed_url.trim()) return;
    setDetecting(true);
    setDetected(null);
    setCsvHeaders([]);
    setMapping({});
    setError('');
    try {
      const r = await api.post('/api/feed/preview-headers', { url: form.feed_url.trim() });
      setDetected(r.data.format);
      if (r.data.format === 'csv') {
        setCsvHeaders(r.data.headers);
        setMapping(autoMap(r.data.headers));
        setShowMapping(true);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo acceder al feed');
    } finally {
      setDetecting(false);
    }
  };

  const validateMapping = () => {
    if (detectedFormat !== 'csv') return true;
    const missing = SERA_FIELDS.filter(f => f.required && !columnMapping[f.key]);
    if (missing.length > 0) {
      setError(`Faltan columnas obligatorias: ${missing.map(f => f.label).join(', ')}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.feed_url.trim()) { setError('La URL es obligatoria'); return; }
    if (!effectiveLang) { setError('Selecciona un idioma'); return; }
    if (!validateMapping()) return;

    // Check if language already used by another feed
    const langAlreadyUsed = !isEdit && existingLangs.includes(effectiveLang);
    const langChanged      = isEdit && editTarget.language !== effectiveLang && existingLangs.filter(l => l !== editTarget.language).includes(effectiveLang);
    if (langAlreadyUsed || langChanged) {
      setError(`Ya existe un feed para el idioma '${effectiveLang}'`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        feed_url:       form.feed_url.trim(),
        feed_name:      form.feed_name.trim() || 'Mi feed',
        language:       effectiveLang,
        is_default:     form.is_default,
        column_mapping: detectedFormat === 'csv' ? columnMapping
                        : (isEdit ? (editTarget.column_mapping || null) : null),
      };
      await onSave(payload, isEdit ? editTarget.feed_config_id : null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">{isEdit ? 'Editar feed' : 'Añadir feed'}</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-300">
          <X size={16} />
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Feed name */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Nombre del feed</label>
            <input
              data-testid="feed-name-input"
              type="text"
              value={form.feed_name}
              onChange={e => setForm({ ...form, feed_name: e.target.value })}
              placeholder="Feed Español"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* Language */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Idioma del feed</label>
            <select
              data-testid="feed-language-select"
              value={form.language}
              onChange={e => setForm({ ...form, language: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-indigo-500"
            >
              {LANGUAGE_OPTIONS.map(o => (
                <option key={o.code} value={o.code}>{o.flag} — {o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Custom language input */}
        {form.language === 'other' && (
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Código de idioma (ISO 639-1)</label>
            <input
              data-testid="feed-custom-lang-input"
              type="text"
              value={form.customLang}
              onChange={e => setForm({ ...form, customLang: e.target.value })}
              placeholder="ej: ca, pl, sv, ro..."
              maxLength={10}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
          </div>
        )}

        {/* Feed URL */}
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">URL del feed</label>
          <div className="flex gap-2">
            <input
              data-testid="feed-url-input"
              type="url"
              value={form.feed_url}
              onChange={e => {
                setForm({ ...form, feed_url: e.target.value });
                if (detectedFormat) { setDetected(null); setCsvHeaders([]); }
              }}
              placeholder="https://mitienda.com/feed.xml  o  /products.csv"
              required
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
            <button
              type="button"
              data-testid="feed-detect-btn"
              onClick={handleDetect}
              disabled={detecting || !form.feed_url.trim()}
              className="flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
            >
              {detecting ? <RefreshCw size={14} className="animate-spin" /> : null}
              {detecting ? 'Detectando...' : 'Detectar formato'}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-1">Compatible con XML (Google Shopping / RSS) y CSV. Sync automático cada 6h.</p>
        </div>

        {/* Format detection result */}
        {detectedFormat && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${
            detectedFormat === 'csv'
              ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400'
              : 'bg-blue-500/5 border-blue-500/20 text-blue-400'
          }`}>
            <FormatBadge format={detectedFormat} />
            <span>
              {detectedFormat === 'csv'
                ? `CSV detectado · ${csvHeaders.length} columnas encontradas`
                : 'XML/RSS detectado · listo para conectar'}
            </span>
          </div>
        )}

        {/* CSV column mapping */}
        {detectedFormat === 'csv' && csvHeaders.length > 0 && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <button
              type="button"
              onClick={() => setShowMapping(v => !v)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-gray-300 text-sm font-medium">Mapeo de columnas CSV</span>
              {showMapping ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
            </button>
            {showMapping && (
              <ColumnMappingForm headers={csvHeaders} mapping={columnMapping} onChange={setMapping} />
            )}
          </div>
        )}

        {/* Is default checkbox */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            data-testid="feed-is-default-checkbox"
            type="checkbox"
            checked={form.is_default}
            onChange={e => setForm({ ...form, is_default: e.target.checked })}
            className="w-4 h-4 accent-indigo-500"
          />
          <span className="text-gray-300 text-sm">Marcar como feed predeterminado (fallback cuando no hay coincidencia de idioma)</span>
        </label>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            type="submit"
            data-testid="feed-save-btn"
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Guardando...</> : <><Check size={14} /> {isEdit ? 'Guardar cambios' : 'Conectar feed'}</>}
          </button>
          <button
            type="button"
            data-testid="feed-cancel-form-btn"
            onClick={onCancel}
            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-5 py-2.5 rounded-lg text-sm font-medium"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function FeedPage() {
  const [configs, setConfigs]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [productCount, setProductCount] = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [editTarget, setEditTarget]     = useState(null);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const pollRef = useRef(null);

  const loadFeeds = useCallback(async (silent = false) => {
    try {
      const r = await api.get('/api/feed');
      setConfigs(r.data.feed_configs || []);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeeds();
    api.get('/api/feed/products/count').then(r => setProductCount(r.data.count)).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [loadFeeds]);

  // Auto-poll while any feed is syncing
  const isSyncing = configs.some(c => c.sync_status === 'syncing');
  useEffect(() => {
    clearInterval(pollRef.current);
    if (isSyncing) {
      pollRef.current = setInterval(() => {
        loadFeeds(true);
        api.get('/api/feed/products/count').then(r => setProductCount(r.data.count)).catch(() => {});
      }, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [isSyncing, loadFeeds]);

  const existingLangs = configs.map(c => c.language).filter(Boolean);

  const handleSave = async (payload, feedConfigId) => {
    try {
      if (feedConfigId) {
        await api.put(`/api/feed/${feedConfigId}`, payload);
        setSuccess('Feed actualizado.');
      } else {
        await api.post('/api/feed', payload);
        setSuccess('Feed creado. Sincronizando productos...');
      }
      setShowForm(false);
      setEditTarget(null);
      await loadFeeds();
      api.get('/api/feed/products/count').then(r => setProductCount(r.data.count)).catch(() => {});
    } catch (e) {
      throw e;
    }
  };

  const handleSync = async (feedConfigId) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/api/feed/${feedConfigId}/sync`);
      setSuccess('Sincronización iniciada...');
      setConfigs(prev => prev.map(c => c.feed_config_id === feedConfigId ? { ...c, sync_status: 'syncing' } : c));
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al sincronizar');
    }
  };

  const handleCancelSync = async (feedConfigId) => {
    try {
      await api.post(`/api/feed/${feedConfigId}/cancel`);
      setSuccess('Cancelación solicitada.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleDelete = async (feedConfigId) => {
    if (!window.confirm('¿Eliminar el feed? Los productos indexados serán desactivados.')) return;
    try {
      await api.delete(`/api/feed/${feedConfigId}`);
      setSuccess('Feed eliminado.');
      await loadFeeds();
      api.get('/api/feed/products/count').then(r => setProductCount(r.data.count)).catch(() => {});
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleSetDefault = async (feedConfigId) => {
    try {
      await api.post(`/api/feed/${feedConfigId}/set-default`);
      setSuccess('Feed marcado como predeterminado.');
      await loadFeeds(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleEditOpen = (config) => {
    setEditTarget(config);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditTarget(null);
    setError('');
  };

  // Compute next sync from earliest last_sync_at
  const getNextSync = () => {
    const synced = configs.filter(c => c.last_sync_at);
    if (!synced.length) return configs.length ? 'Pendiente primera sincronización' : '—';
    const earliest = synced.reduce((a, b) => new Date(a.last_sync_at) < new Date(b.last_sync_at) ? a : b);
    const next = new Date(new Date(earliest.last_sync_at).getTime() + 6 * 3600000);
    const diff = next - new Date();
    if (diff <= 0) return 'Próximamente';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `En ${h}h ${m}m`;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw size={20} className="animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-bold text-white mb-2">Catálogo de productos</h1>
      <p className="text-gray-400 mb-8">
        Conecta uno o varios feeds XML/CSV, uno por idioma. El motor de búsqueda seleccionará automáticamente el feed correcto según el idioma del visitante.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Productos indexados</p>
          <p className="text-white text-2xl font-bold mt-1">{productCount !== null ? productCount.toLocaleString() : '—'}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Feeds configurados</p>
          <p className="text-white text-2xl font-bold mt-1">{configs.length}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Próximo sync</p>
          <p className="text-indigo-400 text-sm font-medium mt-1">{getNextSync()}</p>
          <p className="text-gray-600 text-xs mt-0.5">Cada 6 horas</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Idiomas activos</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {configs.length === 0
              ? <p className="text-gray-500 text-sm">—</p>
              : configs.map(c => (
                  <span key={c.feed_config_id} className="text-xs font-bold text-indigo-300 bg-indigo-500/10 px-1.5 py-0.5 rounded">
                    {(c.language || '??').toUpperCase()}
                  </span>
                ))
            }
          </div>
        </div>
      </div>

      {/* Global messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 text-emerald-400 text-sm mb-4">
          {success}
        </div>
      )}

      {/* Feed list */}
      {configs.length > 0 && (
        <div className="space-y-3 mb-6">
          {configs
            .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
            .map(cfg => (
              <FeedCard
                key={cfg.feed_config_id}
                config={cfg}
                onSync={handleSync}
                onCancelSync={handleCancelSync}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
                onEdit={handleEditOpen}
              />
            ))
          }
        </div>
      )}

      {/* Add feed form or empty state */}
      {showForm ? (
        <FeedForm
          existingLangs={existingLangs}
          editTarget={editTarget}
          onSave={handleSave}
          onCancel={handleFormCancel}
        />
      ) : (
        <button
          data-testid="feed-add-btn"
          onClick={() => { setShowForm(true); setEditTarget(null); setError(''); setSuccess(''); }}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-700 hover:border-indigo-500/50 text-gray-500 hover:text-indigo-400 rounded-xl py-4 text-sm font-medium transition-colors"
        >
          <Plus size={16} /> Añadir feed
        </button>
      )}

      {/* Info section */}
      <div className="mt-8 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-5">
        <h3 className="text-indigo-300 font-medium mb-3">Cómo funciona la selección por idioma</h3>
        <ul className="space-y-1.5 text-sm text-gray-400">
          <li>• El widget detecta el idioma del navegador del visitante (ej: <code className="text-gray-300 bg-gray-800 px-1 rounded">fr-FR</code> → <code className="text-gray-300 bg-gray-800 px-1 rounded">fr</code>)</li>
          <li>• Busca en el feed cuyo idioma coincide con el del navegador</li>
          <li>• Si no hay coincidencia, usa el feed marcado como <span className="text-amber-400">predeterminado</span></li>
          <li>• Si no hay predeterminado, busca en todos los feeds</li>
        </ul>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-400">
          <div>
            <p className="text-blue-400 font-medium mb-1 flex items-center gap-1.5"><FileCode size={13} /> XML (Google Shopping)</p>
            <ul className="space-y-0.5 text-xs">
              <li>• Feed RSS/Atom estándar</li>
              <li>• Campos: g:id, g:title, g:price, g:image_link</li>
            </ul>
          </div>
          <div>
            <p className="text-emerald-400 font-medium mb-1 flex items-center gap-1.5"><FileText size={13} /> CSV</p>
            <ul className="space-y-0.5 text-xs">
              <li>• Delimitador auto-detectado (, ; | tab)</li>
              <li>• Campos mínimos: id, title, price, image_url</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
